import React from 'react'
import { Loader } from '@mantine/core'
import './Button.css'

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'warning' | 'light' // Button variant
  size?: 'xs' | 'sm' | 'md' | 'lg' // Button size
  loading?: boolean // Loading state
  leftIcon?: React.ReactNode // Icon on the left
  rightIcon?: React.ReactNode // Icon on the right
  fullWidth?: boolean // Full width button
  className?: string // Custom class name
  children: React.ReactNode // Children content
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  loading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  className = '',
  disabled,
  children,
  ...props
}) => {
  // Base button styles
  const baseStyles = 'btn d-inline-flex align-items-center justify-content-center gap-2 font-weight-600 position-relative'

  // Variant styles
  const variantStyles = {
    primary: 'btn-primary',
    secondary: 'btn-secondary',
    outline: 'btn-outline-primary',
    ghost: 'btn-ghost',
    danger: 'btn-danger',
    warning: 'btn-warning',
    light: 'btn-light'
  }

  // Size styles
  const sizeStyles = {
    xs: 'btn-xs',
    sm: 'btn-sm',
    md: '',
    lg: 'btn-lg'
  }

  // Combine all classes
  const buttonClasses = [
    baseStyles,
    variantStyles[variant],
    sizeStyles[size],
    fullWidth ? 'w-100' : '',
    loading ? 'btn-loading' : '',
    className
  ].filter(Boolean).join(' ')

  return (
    <button
      className={buttonClasses}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <Loader 
          size={size === 'xs' ? 12 : size === 'sm' ? 14 : size === 'lg' ? 18 : 16} 
          color="currentColor" 
        />
      )}
      {!loading && leftIcon && <span className="btn-icon">{leftIcon}</span>}
      <span>{children}</span>
      {!loading && rightIcon && <span className="btn-icon">{rightIcon}</span>}
    </button>
  )
}

export default Button
